import { flexRender, type Table as TableType } from '@tanstack/react-table'
import { Play } from 'lucide-react'
import {
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
} from 'react-aria-components'
import { cn } from '@/lib/utils'

const SORTED_ICON_MAP = {
  desc: <Play fill="#979791" size={10} className="rotate-90 border-0" />,
  asc: <Play fill="#979791" size={10} className="rotate-270" />,
}

function CallLogsTableV2<T>({
  table,
  isRefreshing,
}: {
  table: TableType<T & { c_id: string }>
  isRefreshing: boolean
}) {
  'use no memo'
  return (
    <Table className="w-full border-separate border-spacing-y-1">
      <TableHeader className="sticky top-0 z-50 mb-2 bg-gray-50">
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isSorted
                = header.column.getIsSorted() as keyof typeof SORTED_ICON_MAP

              return (
                <Column
                  key={header.id}
                  style={{ width: `${header.getSize()}px` }}
                  isRowHeader={true}
                  className={cn(
                    `
                      truncate px-4 py-3 text-left text-xs font-semibold
                      text-gray-500 uppercase
                    `,
                    'border-0 bg-transparent',
                    `
                      cursor-pointer
                      hover:text-gray-900
                    `,
                    `
                      focus-visible:ring-2 focus-visible:ring-offset-2
                      focus-visible:outline-none
                    `,
                    'transition-colors',
                  )}
                >
                  <div
                    className="flex h-full w-full items-center gap-2"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {SORTED_ICON_MAP[isSorted]}
                  </div>
                </Column>
              )
            })}
          </tr>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map(row => (
          <Row
            key={row.id}
            onAction={() => row.getToggleSelectedHandler()(row.id)}
            className={cn(
              'relative rounded-md border border-gray-300/60 bg-white',
              `
                cursor-pointer transition-all duration-200
                hover:bg-gray-50
              `,
              row.getIsSelected()
              && `
                bg-gray-200
                hover:bg-gray-200
                active:bg-gray-100
              `,
              isRefreshing && 'animate-pulse',
              row.depth === 1
              && `
                bg-gray-100
                hover:bg-gray-200
              `,
            )}
          >
            {row.getVisibleCells().map(cell => (
              <Cell
                key={cell.id}
                className={cn(
                  'relative truncate px-4 py-4 text-sm text-gray-500',
                  `
                    border-t-1 border-b-1 border-gray-300/60
                    first:border-l-1
                    last:border-r-1
                  `,
                  `
                    first:rounded-l-md
                    last:rounded-r-md
                  `,
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Cell>
            ))}
          </Row>
        ))}
      </TableBody>
    </Table>
  )
}

export default CallLogsTableV2
